import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSiteUrl, seoDataByPath } from './config';
import { injectJsonLd } from './jsonld';

const MANAGED_META_KEYS = [
  'description',
  'keywords',
  'robots',
  'og:title',
  'og:description',
  'og:image',
  'og:type',
  'og:url',
  'twitter:card',
  'twitter:title',
  'twitter:description',
  'twitter:image',
];

function isOgKey(key: string): boolean {
  return key.startsWith('og:');
}

function setMetaTag(key: string, content: string): void {
  const attr = isOgKey(key) ? 'property' : 'name';
  const selector = isOgKey(key)
    ? `meta[property="${key}"]`
    : `meta[name="${key}"]`;

  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMetaTag(key: string): void {
  const selector = isOgKey(key)
    ? `meta[property="${key}"]`
    : `meta[name="${key}"]`;
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

function setCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applySeo(path: string): void {
  const data = seoDataByPath[path];

  // Clear all managed tags so metadata never leaks between routes.
  MANAGED_META_KEYS.forEach((key) => removeMetaTag(key));
  document.head.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());

  if (!data) {
    return;
  }

  document.title = data.title;

  if (data.noindex) {
    setMetaTag('robots', 'noindex, nofollow');
    return;
  }

  if (data.description) {
    setMetaTag('description', data.description);
  }
  if (data.keywords) {
    setMetaTag('keywords', data.keywords);
  }

  const siteUrl = getSiteUrl();
  const canonical = siteUrl ? `${siteUrl}${data.path}` : '';
  if (canonical) {
    setCanonical(canonical);
  }

  setMetaTag('og:title', data.title);
  setMetaTag('og:description', data.description);
  setMetaTag('og:type', data.ogType || 'website');
  if (data.ogImage) {
    setMetaTag('og:image', data.ogImage);
  }
  if (canonical) {
    setMetaTag('og:url', canonical);
  }

  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', data.title);
  setMetaTag('twitter:description', data.description);
  if (data.ogImage) {
    setMetaTag('twitter:image', data.ogImage);
  }
}

export function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    applySeo(path);
    injectJsonLd(path);
  }, [location.pathname]);

  return null;
}